/**
 * The new starter flow, as pure functions (2 Oct).
 *
 * A case is HR's New User Request and the requests that follow it; the server
 * derives every stage from Ivanti on read. What lives here is presentation:
 * which mark a stage gets, which words describe it, where the "Raise …" buttons
 * go, and how a prefill from the case becomes the request form's first answers.
 *
 * Import-free (types only) so `tests/starters.test.mjs` runs it without React.
 */

/** A stage's state, as the server sends it. */
export type StarterStageState = "DONE" | "IN_PROGRESS" | "WAITING" | "NOT_STARTED" | "NOT_NEEDED";

/** The five stages, in order. */
export const STARTER_STAGE_KEYS = ["hr", "account", "it_setup", "systems", "crm"] as const;
export type StarterStageKey = (typeof STARTER_STAGE_KEYS)[number];

/** The requests the case page can open prefilled. */
export const STARTER_STEPS = ["it_setup", "systems", "crm"] as const;
export type StarterStep = (typeof STARTER_STEPS)[number];

/** The reader's possible next actions, as the server names them. */
export const STARTER_ACTIONS = [
  "confirm_account",
  "raise_it_setup",
  "raise_systems",
  "decide_crm",
] as const;
export type StarterActionName = (typeof STARTER_ACTIONS)[number];

/** The tracker's marks: ✅ done, 🔄 under way, ⏳ waiting on something outside, ⬜ not yet. */
const STAGE_MARKS: Record<StarterStageState, string> = {
  DONE: "✅",
  IN_PROGRESS: "🔄",
  WAITING: "⏳",
  NOT_STARTED: "⬜",
  NOT_NEEDED: "➖",
};

/** The mark for a stage state; an unknown state reads as "not yet". */
export function stageMark(state: string | null | undefined): string {
  return STAGE_MARKS[(state ?? "") as StarterStageState] ?? STAGE_MARKS.NOT_STARTED;
}

/** `Timeline` status for a stage state: done is complete, under way is current. */
export function stageTimelineStatus(
  state: string | null | undefined,
): "complete" | "current" | "upcoming" {
  if (state === "DONE" || state === "NOT_NEEDED") {
    return "complete";
  }
  return state === "IN_PROGRESS" || state === "WAITING" ? "current" : "upcoming";
}

/**
 * The translation key for a stage's status words.
 *
 * Request stages say "Not raised" / "Not needed" / "Cancelled" in our words;
 * once raised, the page shows Ivanti's own status instead (null here).
 */
export function stageStatusKey(stage: {
  key: string;
  status: string;
}): `starters.status.${string}` | null {
  if (stage.key === "account") {
    return `starters.status.account.${stage.status}`;
  }
  if (stage.key === "hr" || stage.status === "raised") {
    return null;
  }
  return `starters.status.${stage.status}`;
}

/** Where a step's prefilled request form opens. */
export function starterFormPath(caseId: string, step: StarterStep): string {
  const query = new URLSearchParams({ kind: "SERVICE_REQUEST", starter: caseId, step });
  return `/tickets/new?${query.toString()}`;
}

/** The step the create page was opened for, or null when the URL names none (or a bogus one). */
export function starterStepFromParams(
  starter: string | null,
  step: string | null,
): { caseId: string; step: StarterStep } | null {
  if (starter === null || starter === "" || step === null) {
    return null;
  }
  return (STARTER_STEPS as readonly string[]).includes(step)
    ? { caseId: starter, step: step as StarterStep }
    : null;
}

/** The step a reader's action is about, so its button can be the primary one. */
export function stepForAction(action: string): StarterStep | null {
  switch (action) {
    case "raise_it_setup":
      return "it_setup";
    case "raise_systems":
      return "systems";
    case "decide_crm":
      return "crm";
    default:
      return null;
  }
}

/** The steps a case may raise now: none before the gate, Business Systems only if needed. */
export function availableSteps(caseView: {
  account: { state: string };
  system_access_needed?: boolean | null;
}): StarterStep[] {
  if (caseView.account.state !== "ready") {
    return [];
  }
  return STARTER_STEPS.filter(
    (step) => step !== "systems" || caseView.system_access_needed !== false,
  );
}

// ─── Prefill ───────────────────────────────────────────────────────────────

/** The parts of an `OfferingParameter` the prefill touches. */
export interface StarterPrefillParameter {
  parameter_id: string;
  lookup?: string | null;
  default_value?: string | null;
  default_display_value?: string | null;
}

/** One prefill answer, as `GET /starters/{id}/prefill/{step}` sends it. */
export interface StarterPrefillAnswer {
  parameter_id: string;
  value?: string | null;
  value_ids?: string[] | null;
  display_value?: string | null;
  /** Answered by the case itself: submitted, not asked (5 Oct). */
  hidden?: boolean | null;
}

/**
 * The parameters the case answers so the form need not ask them (5 Oct):
 * "Is the new starter in ITSM yet?" once the account is confirmed. Only
 * those the prefill really filled in (`applied`), so a value the form
 * refused never leaves a question both unanswered and unseen.
 */
export function answeredByCase(
  values: readonly StarterPrefillAnswer[] | null | undefined,
  applied: Iterable<string>,
): Set<string> {
  const filled = new Set(applied);
  return new Set(
    (values ?? [])
      .filter((value) => value.hidden === true && filled.has(value.parameter_id))
      .map((value) => value.parameter_id),
  );
}

/**
 * Make the case's people the defaults of the form's person pickers.
 *
 * A directory picker shows the name from `default_display_value` and submits
 * the RecId from `default_value` — the same path that opens a request on the
 * requester — so a starter, manager or reference user arrives named, not as a
 * GUID. Every other field is untouched here; `applyPrefill` fills those, and
 * accepts a lookup item only when it names the parameter's own default, which
 * this sets. Returns a new list; the input is not modified.
 */
export function withStarterPeople<P extends StarterPrefillParameter>(
  parameters: readonly P[],
  values: readonly StarterPrefillAnswer[] | null | undefined,
): P[] {
  const byId = new Map((values ?? []).map((value) => [value.parameter_id, value]));
  return parameters.map((parameter) => {
    const value = byId.get(parameter.parameter_id);
    if (value === undefined || parameter.lookup == null || parameter.lookup === "") {
      return parameter;
    }
    const id = (value.value_ids ?? [])[0] ?? value.value ?? "";
    if (id === "") {
      return parameter;
    }
    return { ...parameter, default_value: id, default_display_value: value.display_value ?? id };
  });
}

/**
 * A form's start date ("2026-11-01") as a local calendar date.
 *
 * Parsed by hand: `new Date("2026-11-01")` is midnight UTC, which a reader in
 * Wisconsin would be shown as 31 October. Null for anything that is not one.
 */
export function startDateValue(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  if (match === null) {
    return null;
  }
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** What the New starter page does for one case (6 Oct, product owner: one page per person). */
export interface StarterPageMode<A extends string = string> {
  /** Go to the case's access page instead (client-side, replacing this entry). */
  redirectTo: string | null;
  /** Show the "Next requests" buttons (off while the access front door is on). */
  showNextRequests: boolean;
  /** Which of the reader's actions to list: only "confirm the account" while it is on. */
  actions: readonly A[];
}

/**
 * The access front door on: a case raised there has one page, under Access
 * requests, so the starter page redirects to it; any other New case keeps
 * this page but without the "Next requests" buttons (they would duplicate the
 * Access page's steps and risk filing twice). Off: exactly as before.
 *
 * 28 Oct (Rusty): on the new forms (`newForms`), a case the front door did
 * not raise (the 2 Oct starter flow) has no page of its own either: it lands
 * on the Access list.
 */
export function starterPageMode<A extends string>(
  frontDoor: boolean,
  accessPath: string | null | undefined,
  actions: readonly A[] | null | undefined,
  newForms = false,
): StarterPageMode<A> {
  const all = actions ?? [];
  if (!frontDoor) {
    return { redirectTo: null, showNextRequests: true, actions: all };
  }
  return {
    redirectTo: accessPath != null && accessPath !== "" ? accessPath : newForms ? "/access" : null,
    showNextRequests: false,
    actions: all.filter((action) => action === "confirm_account"),
  };
}
